import { DEFAULT_LABEL_CHANGES, RESET_CONFIG } from "../consts/routeDetails";
import { LAYERS, OUTER, POSTCODE, SECTOR } from "../consts/layers";
import {
  POSTCODE_DISTRICT_BOUNDARIES_ID,
  POSTCODE_SECTOR_BOUNDARIES_ID,
  POSTCODE_UNIT_BOUNDARIES_ID,
} from "../consts/sources";
import { isNil, each, filter, groupBy, map, reduce } from "lodash";
import { groupPostcodes, groupSectors } from "./lookUpTables";

import { DEFAULT_TIMEBAND } from "../consts/strings";
import { Postcode } from "./postcode";
import { isRedirectionRouteCode, isRouteCode } from "./routes";
import moment from "moment";
import { getRedirectForPostcode } from "./redirectConfig";

export const timeBandsSorter = (a, b) => {
  if (a.plannedReadyTimeBand === "default") {
    return 1;
  }
  if (b.plannedReadyTimeBand === "default") {
    return -1;
  }
  const aTime = moment(a.plannedReadyTimeBand).format("HH:mm");
  const bTime = moment(b.plannedReadyTimeBand).format("HH:mm");
  return aTime > bTime ? 1 : -1;
};

const setFeatureState = ({
  map,
  key,
  type = OUTER,
  labelChanges = { ...DEFAULT_LABEL_CHANGES },
  lookupTable,
  multi,
  postcodeToRoute,
  config,
  heatmap,
  reset,
  checkInheritance = true,
  artificialFeatures,
}) => {
  const CONFIG = ROUTE_CONFIG_MAPPING[type];
  const id = CONFIG.getId(key, lookupTable);

  if (isNil(id)) return;
  if (!heatmap && id) {
    map.setFeatureState(
      {
        id,
        source: CONFIG.SOURCE,
        sourceLayer: CONFIG.SOURCE_LAYER,
      },
      {
        ...config,
        root: key,
      }
    );
  } else if (id === 0 && !heatmap)
    artificialFeatures[key] = { ...config, root: key };

  const route = config.routeCode || null;

  if (
    checkInheritance &&
    (reset || isInherited({ key, type, postcodeToRoute, route }))
  ) {
    return;
  }

  // postcode to route
  postcodeToRoute[type][key] = route;

  // generate labelChanges
  if (route) {
    if (labelChanges[type][route]) {
      labelChanges[type][route].push(key);
    } else {
      labelChanges[type][route] = [key];
    }
  }
  // generate multi
  if (!multi) return;
  if (type === OUTER) {
    const outerLevel = multi[key] ?? { sector: {}, routes: new Set() };
    outerLevel.routes.add(route);
    multi[key] = outerLevel;
  }
  if (type === SECTOR) {
    const [outer] = key.split(" ");
    const outerLevel = multi[outer] ?? { sector: {}, routes: new Set() };
    const sectorLevel = {
      sector: key,
      routes: new Set(),
    };
    outerLevel.routes.add(route);
    sectorLevel.routes.add(route);
    multi[outer] = {
      ...outerLevel,
      sector: { ...outerLevel.sector, [key]: sectorLevel },
    };
  }
  if (type === POSTCODE) {
    const sector = key.substr(0, key.length - 2);
    const outer =
      sector.split(" ").length > 1
        ? sector.split(" ")[0]
        : sector.substr(0, sector.length - 1);
    const outerLevel = multi[outer] ?? { sector: {}, routes: new Set() };
    const sectorLevel = outerLevel.sector[sector] ?? {
      routes: new Set(),
    };
    outerLevel.routes.add(route);
    sectorLevel.routes.add(route);
    multi[outer] = {
      ...outerLevel,
      sector: { ...outerLevel.sector, [sector]: sectorLevel },
    };
  }
};

const ROUTE_CONFIG_MAPPING = {
  OUTER: {
    getId: (key, table) => table && table[key]?.id,
    SOURCE: POSTCODE_DISTRICT_BOUNDARIES_ID,
    SOURCE_LAYER: LAYERS.OUTER.BOUNDARIES.SOURCE_LAYER,
  },
  SECTOR: {
    getId: (key, table) => table && table[key]?.id,
    SOURCE: POSTCODE_SECTOR_BOUNDARIES_ID,
    SOURCE_LAYER: LAYERS.SECTOR.BOUNDARIES.SOURCE_LAYER,
  },
  POSTCODE: {
    getId: (key, table) => table && table[key]?.id,
    SOURCE: POSTCODE_UNIT_BOUNDARIES_ID,
    SOURCE_LAYER: LAYERS.POSTCODE.BOUNDARIES.SOURCE_LAYER,
  },
};

export const processOuterConfig = ({
  map,
  outerConfig,
  reset = false,
  heatmap = false,
  routes = [],
  labelChanges,
  lookupTables,
  multi,
  postcodeToRoute = { OUTER: {}, SECTOR: {}, POSTCODE: {} },
  selectedTimeBand,
  propagate = true,
  artificialFeatures,
}) => {
  // set the outer
  setFeatureState({
    map,
    key: outerConfig.outer,
    type: OUTER,
    labelChanges,
    lookupTable: lookupTables?.OUTER,
    multi,
    postcodeToRoute,
    checkInheritance: propagate,
    config: !reset
      ? {
          ...outerConfig,
          colour: getRouteColour({
            config: outerConfig,
            routes,
            selectedTimeBand,
          }),
        }
      : { ...RESET_CONFIG },
    reset,
    artificialFeatures,
  });
  if (propagate) {
    // propagate outer route to sector
    propagateToSector({
      map,
      outerConfig: !reset ? outerConfig : { ...outerConfig, ...RESET_CONFIG },
      routes,
      labelChanges,
      lookupTables,
      multi,
      postcodeToRoute,
      heatmap,
      reset,
      selectedTimeBand,
      checkInheritance: propagate,
      artificialFeatures,
    });
  }
};

export const processSectorConfig = ({
  map,
  sectorConfig,
  redirectConfig,
  reset = false,
  heatmap = false,
  routes = [],
  labelChanges,
  lookupTables,
  multi,
  postcodeToRoute,
  selectedTimeBand,
  propagate,
  artificialFeatures,
}) => {
  const redirect = isRedirectionRouteCode(sectorConfig.routeCode);
  let config;
  if (reset) config = { ...RESET_CONFIG };
  else if (!redirect)
    config = {
      ...sectorConfig,
      colour: getRouteColour({
        config: sectorConfig,
        routes,
        selectedTimeBand,
      }),
    };
  else {
    const redirectToRouteCode = getRedirectForPostcode({
      redirectConfig,
      redirectionRouteCode: sectorConfig.routeCode,
      postcode: sectorConfig.sector,
    });
    config = {
      ...sectorConfig,
      colour: getRouteColour({
        config: { ...sectorConfig, routeCode: redirectToRouteCode },
        routes,
        selectedTimeBand,
      }),
      routeCode: redirectToRouteCode,
    };
  }

  setFeatureState({
    map,
    key: sectorConfig.sector,
    type: SECTOR,
    labelChanges,
    lookupTable: lookupTables?.SECTOR,
    multi,
    postcodeToRoute,
    checkInheritance: propagate && !redirect,
    config,
    reset,
    artificialFeatures,
  });

  if (propagate)
    // propagate sector route to postcode
    propagateToPostcode({
      map,
      sector: sectorConfig.sector,
      config: !reset ? sectorConfig : { ...RESET_CONFIG },
      redirectConfig,
      routes,
      labelChanges,
      lookupTables,
      multi,
      postcodeToRoute,
      heatmap,
      reset,
      selectedTimeBand,
      checkInheritance: propagate && !redirect,
      artificialFeatures,
    });
};

export const processPostcodeConfig = ({
  map,
  postcodeConfig,
  redirectConfig,
  reset = false,
  heatmap = false,
  routes = [],
  labelChanges,
  lookupTables,
  multi,
  postcodeToRoute,
  selectedTimeBand,
  propagate,
  artificialFeatures,
}) => {
  const redirect = isRedirectionRouteCode(postcodeConfig.routeCode);
  let config;
  if (reset) config = { ...RESET_CONFIG };
  else if (!redirect)
    config = {
      ...postcodeConfig,
      colour: getRouteColour({
        config: postcodeConfig,
        routes,
        selectedTimeBand,
      }),
    };
  else {
    const redirectToRouteCode = getRedirectForPostcode({
      redirectConfig,
      redirectionRouteCode: postcodeConfig.routeCode,
      postcode: postcodeConfig.postcode,
    });
    config = {
      ...postcodeConfig,
      colour: getRouteColour({
        config: { ...postcodeConfig, routeCode: redirectToRouteCode },
        routes,
        selectedTimeBand,
      }),
      routeCode: redirectToRouteCode,
    };
  }
  setFeatureState({
    map,
    key: postcodeConfig.postcode,
    type: POSTCODE,
    labelChanges,
    lookupTable: lookupTables?.POSTCODE,
    multi,
    postcodeToRoute,
    checkInheritance: propagate && !redirect,
    config,
    reset,
    artificialFeatures,
  });
};

const propagateToSector = ({
  map,
  outerConfig,
  routes = [],
  labelChanges,
  lookupTables,
  multi,
  postcodeToRoute,
  heatmap,
  reset = false,
  selectedTimeBand,
  checkInheritance,
  artificialFeatures,
}) => {
  const sectors = groupSectors(lookupTables?.SECTOR)[outerConfig.outer] || [];

  sectors.forEach((sector) => {
    setFeatureState({
      map,
      key: sector,
      type: SECTOR,
      labelChanges,
      lookupTable: lookupTables?.SECTOR,
      multi,
      postcodeToRoute,
      checkInheritance,
      config: {
        ...outerConfig,
        colour: getRouteColour({
          config: outerConfig,
          routes,
          selectedTimeBand,
        }),
      },
      heatmap,
      reset,
      artificialFeatures,
    });
    // propagate outer route to postcode
    propagateToPostcode({
      map,
      sector,
      config: outerConfig,
      routes,
      labelChanges,
      lookupTables,
      multi,
      postcodeToRoute,
      heatmap,
      reset,
      selectedTimeBand,
      checkInheritance,
      artificialFeatures,
    });
  });
};

export const propagateToPostcode = ({
  map,
  sector,
  config,
  redirectConfig,
  routes = [],
  labelChanges,
  lookupTables,
  multi,
  postcodeToRoute,
  heatmap,
  reset = false,
  selectedTimeBand,
  checkInheritance,
  artificialFeatures,
}) => {
  const redirect = isRedirectionRouteCode(config.routeCode);
  const postcodes = groupPostcodes(lookupTables?.POSTCODE)[sector] || [];

  postcodes.forEach((postcode) => {
    let postcodeConfig;
    if (!redirect)
      postcodeConfig = {
        ...config,
        colour: getRouteColour({ config, routes, selectedTimeBand }),
      };
    else {
      const redirectToRouteCode = getRedirectForPostcode({
        redirectConfig,
        redirectionRouteCode: config.routeCode,
        postcode,
      });
      postcodeConfig = {
        ...config,
        colour: getRouteColour({
          config: { ...config, routeCode: redirectToRouteCode },
          routes,
          selectedTimeBand,
        }),
        routeCode: redirectToRouteCode,
      };
    }

    setFeatureState({
      map,
      key: postcode,
      type: POSTCODE,
      labelChanges,
      lookupTable: lookupTables?.POSTCODE,
      multi,
      postcodeToRoute,
      checkInheritance,
      config: postcodeConfig,
      heatmap,
      reset,
      artificialFeatures,
    });
  });
};

const isInherited = ({ key, type, postcodeToRoute, route }) => {
  if ((route && isRouteCode(route)) || type === OUTER) return false;
  const postcode = new Postcode(key);
  return type === SECTOR
    ? checkForSector({ postcode, postcodeToRoute, route })
    : checkForPostcode({ postcode, postcodeToRoute, route });
};

const checkForSector = ({ postcode, postcodeToRoute, route }) => {
  return (
    postcodeToRoute[OUTER][postcode.toOuter()] &&
    isRouteCode(postcodeToRoute[OUTER][postcode.toOuter()]) &&
    (!route || postcodeToRoute[OUTER][postcode.toOuter()] === route)
  );
};

const checkForPostcode = ({ postcode, postcodeToRoute, route }) => {
  return (
    (postcodeToRoute[OUTER][postcode.toOuter()] &&
      isRouteCode(postcodeToRoute[OUTER][postcode.toOuter()]) &&
      (!route || postcodeToRoute[OUTER][postcode.toOuter()] === route)) ||
    (postcodeToRoute[SECTOR][postcode.toSector()] &&
      isRouteCode(postcodeToRoute[SECTOR][postcode.toSector()]) &&
      (!route || postcodeToRoute[SECTOR][postcode.toSector()] === route))
  );
};

const getRouteColour = ({ config, routes, selectedTimeBand }) => {
  if (!config.routeCode) return;

  const route = routes.find((route) => route.routeCode === config.routeCode);
  if (!route) return;

  return !selectedTimeBand ||
    route.plannedReadyTimeAsString === selectedTimeBand ||
    (selectedTimeBand === DEFAULT_TIMEBAND && !route.plannedReadyTimeAsString)
    ? route.colour
    : null;
};

export const generateRouteConfigFromRoutes = ({ routes, type }) => {
  const routesForType = groupBy(
    map(
      filter(routes, (route) => route.routeType === type),
      (route) => ({
        ...route,
        plannedReadyTime: route.plannedReadyTime || "default",
      })
    ),
    "plannedReadyTime"
  );
  const routeConfig = [];

  Object.keys(routesForType).forEach((timeband) => {
    const outers = [];
    const sectors = [];
    const postcodes = [];

    const routes = routesForType[timeband];
    each(routes, (route) => {
      each(route.outer, (outer) => {
        outers.push({ outer: outer.postcodeOuter, routeCode: route.routeCode });
      });
      each(route.sector, (sector) => {
        sectors.push({
          sector: sector.postcodeSector,
          routeCode: route.routeCode,
        });
      });
      each(route.postcode, (postcode) => {
        postcodes.push({
          postcode: postcode.postcode,
          routeCode: route.routeCode,
        });
      });
    });

    const sectorsByOuter = groupBy(sectors, (sector) =>
      Postcode.toOuter(sector.sector)
    );
    const postcodesByOuter = groupBy(postcodes, (postcode) =>
      Postcode.toOuter(postcode.postcode)
    );
    const outersByOuter = reduce(
      outers,
      (prev, curr) => {
        prev[curr.outer] = curr;
        return prev;
      },
      {}
    );
    each(Object.keys(sectorsByOuter), (outer) => {
      outersByOuter[outer] = outersByOuter[outer] || { outer };
      outersByOuter[outer].sector = !!outersByOuter[outer].sector
        ? [...outersByOuter[outer].sector, ...sectorsByOuter[outer]]
        : [...sectorsByOuter[outer]];
    });
    each(Object.keys(postcodesByOuter), (outer) => {
      outersByOuter[outer] = outersByOuter[outer] || { outer };
      outersByOuter[outer].postcode = !!outersByOuter[outer].postcode
        ? [...outersByOuter[outer].postcode, ...postcodesByOuter[outer]]
        : [...postcodesByOuter[outer]];
    });
    routeConfig.push({
      plannedReadyTimeBand: timeband,
      outer: Object.values(outersByOuter).map((o) => ({
        ...o,
        routeType: type,
      })),
    });
  });
  return routeConfig;
};

export const generateCollectionAreaConfigFromAreas = ({ collectionAreas }) => {
  const config = {};
  Object.keys(collectionAreas).forEach((areaType) => {
    config[areaType.toLowerCase()] = Object.keys(collectionAreas[areaType]).map(
      (areaKey) => {
        const data = { ...collectionAreas[areaType][areaKey] };
        delete data.defaultPlannedRoute;
        delete data.defaultSameDayRoute;
        delete data.defaultPlannedColour;
        delete data.defaultSameDayColour;
        return data;
      }
    );
  });
  return config;
};
